import React, {useEffect, useRef} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {AGENCY_ACCOUNTS_PATH, AGENCY_PORTAL_USAGE_PATH, AGENCY_SECTIONS} from '../../../layout/v2/agencyPages';
import {NS, addonPriceText, formatAmount, listMonthly, meterOf, quotaNoticeOf} from './subscriptionPageModel';
import {Pill} from './SubscriptionParts';
import {recordSubscriptionEvent} from './subscriptionRequests';

const RAW = {escapeValue: false};
const TEAM_PATH = (AGENCY_SECTIONS.find((section) => section.key === 'team') || {}).to;

/**
 * «Lo que usas de tu plan» (mockup 1, segunda tarjeta). Los topes son los REALES de la agencia (los
 * negociados incluidos), que es lo que hace cumplir el backend; los usuarios van sin tope hasta que exista
 * su medidor (D-6). Sin `usage` (Backend viejo) la tarjeta no se dibuja: la decide el padre.
 */
export function UsageCard({usage, nextPlan, comparison, onSeeNextPlan, onRequestMoreAccounts, onCustomPlan}) {
    const {t} = useTranslation();
    const accounts = meterOf(usage.ad_accounts);
    const portal = meterOf(usage.portal_accesses);
    const notice = quotaNoticeOf(usage);
    const teamUsed = usage.team_users && usage.team_users.used;

    return (
        <section className="sp-card" aria-labelledby="sp-usage-title">
            <h2 id="sp-usage-title" className="sp-card__title">{t(`${NS}.usage.title`)}</h2>
            <div className="sp-meters">
                <MeterRow meter={accounts} entry={usage.ad_accounts} label={t(`${NS}.usage.ad_accounts`)}
                          help={t(`${NS}.usage.ad_accounts_help`)} linkTo={AGENCY_ACCOUNTS_PATH}
                          linkLabel={t(`${NS}.usage.see_accounts`)}/>
                {typeof teamUsed === 'number' && (
                    <MeterRow label={t(`${NS}.usage.team_users`)} help={t(`${NS}.usage.team_users_help`)}
                              count={t(`${NS}.usage.team_count`, {count: teamUsed})}
                              linkTo={TEAM_PATH} linkLabel={t(`${NS}.usage.see_team`)}/>
                )}
                <MeterRow meter={portal} entry={usage.portal_accesses} label={t(`${NS}.usage.portal`)}
                          help={t(`${NS}.usage.portal_help`)} linkTo={AGENCY_PORTAL_USAGE_PATH}
                          linkLabel={t(`${NS}.usage.see_portal`)}/>
            </div>
            {notice && (
                <QuotaNotice notice={notice} nextPlan={nextPlan} comparison={comparison}
                             onSeeNextPlan={onSeeNextPlan} onRequestMoreAccounts={onRequestMoreAccounts}
                             onCustomPlan={onCustomPlan}/>
            )}
        </section>
    );
}

/**
 * Una fila. Con tope: barra + «64 de 70» + la pastilla desde el 90 % (nunca sólo el color). Sin tope
 * (usuarios, o un tope en 0): la cantidad sola, sin barra.
 */
function MeterRow({meter, entry, count, label, help, linkTo, linkLabel}) {
    const {t} = useTranslation();
    const state = meter ? meter.state : 'none';
    let number = count;
    if (meter) number = t(`${NS}.usage.of`, {used: meter.used, limit: meter.limit, interpolation: RAW});
    else if (entry && typeof entry.used === 'number') number = String(entry.used);
    if (number === undefined) return null;

    return (
        <div className={`sp-meter -${state}`} data-testid="sp-meter">
            <div className="sp-meter__main">
                <div className="sp-meter__top">
                    <span className="sp-meter__label">{label}</span>
                    {state === 'near' && <Pill tone="warn">{t(`${NS}.usage.left`, {count: meter.left})}</Pill>}
                    {state === 'full' && <Pill tone="bad">{t(`${NS}.usage.full`)}</Pill>}
                </div>
                {meter && (
                    // El ancho de la barra es un DATO: va como variable CSS; el estilo vive en el parcial.
                    <div className="sp-meter__track" role="img" aria-label={`${number} · ${label}`}>
                        <div className="sp-meter__fill" style={{'--sp-meter-fill': `${meter.percent}%`}}/>
                    </div>
                )}
                <p className="sp-meter__help">
                    {help}
                    {linkTo && <> <Link to={linkTo} className="sp-link">{linkLabel}</Link></>}
                </p>
            </div>
            <div className="sp-meter__num">{number}</div>
        </div>
    );
}

/** El aviso de cupo, desde el 90 % de cuentas: dos salidas, sumar cuentas o pasar al plan siguiente. */
function QuotaNotice({notice, nextPlan, comparison, onSeeNextPlan, onRequestMoreAccounts, onCustomPlan}) {
    const {t, i18n} = useTranslation();
    const shown = useRef(false);
    useEffect(() => {
        if (shown.current) return;
        shown.current = true;
        recordSubscriptionEvent('quota_notice_shown');
    }, []);

    const title = notice.state === 'full'
        ? t(`${NS}.usage.notice_full`)
        : t(`${NS}.usage.notice`, {count: notice.left});

    return (
        <div className="sp-notice" data-testid="sp-quota-notice">
            <p className="sp-notice__title">{title}</p>
            <div className="sp-notice__options">
                <div className="sp-option">
                    <div>
                        <p className="sp-option__title">{t(`${NS}.usage.more_accounts_title`)}</p>
                        <p className="sp-option__body">{t(`${NS}.usage.more_accounts_body`, {
                            price: addonPriceText(t, 'accounts', {currency: comparison.currency, lang: i18n && i18n.language}),
                            interpolation: RAW,
                        })}</p>
                    </div>
                    <button type="button" className="btn -alt -negative" onClick={onRequestMoreAccounts}>
                        {t(`${NS}.usage.more_accounts_cta`)}
                    </button>
                </div>
                {nextPlan ? (
                    <NextPlanOption plan={nextPlan} comparison={comparison} lang={i18n && i18n.language}
                                    onSeeNextPlan={onSeeNextPlan}/>
                ) : (
                    <div className="sp-option">
                        <div>
                            <p className="sp-option__title">{t(`${NS}.plans.custom_title`)}</p>
                            <p className="sp-option__body">{t(`${NS}.plans.custom_body`)}</p>
                        </div>
                        <button type="button" className="btn -alt -negative" onClick={onCustomPlan}>
                            {t(`${NS}.usage.custom_cta`)}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function NextPlanOption({plan, comparison, lang, onSeeNextPlan}) {
    const {t} = useTranslation();
    const name = t(plan.nameKey);
    const {quantities} = plan;
    const price = formatAmount(listMonthly(plan.id, comparison.currency, 'monthly'), comparison.currency, lang);
    const body = quantities.teamUsers === null
        ? t(`${NS}.usage.bigger_plan_body_unlimited`, {
            accounts: quantities.linkedAccounts, portal: quantities.portalAccesses, price, interpolation: RAW,
        })
        : t(`${NS}.usage.bigger_plan_body`, {
            accounts: quantities.linkedAccounts, users: quantities.teamUsers, portal: quantities.portalAccesses,
            price, interpolation: RAW,
        });
    return (
        <div className="sp-option">
            <div>
                <p className="sp-option__title">{t(`${NS}.usage.bigger_plan_title`, {plan: name, interpolation: RAW})}</p>
                <p className="sp-option__body">{body}</p>
            </div>
            <button type="button" className="btn -alt -negative" onClick={onSeeNextPlan}>
                {t(`${NS}.usage.bigger_plan_cta`, {plan: name, interpolation: RAW})}
            </button>
        </div>
    );
}
